import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Search, TrendingUp, TrendingDown, Minus, Globe, FileText, Zap, AlertTriangle,
  CheckCircle, Clock, ArrowLeft, RefreshCw, Sparkles, Target, Link as LinkIcon,
  BarChart3, Activity, ExternalLink, Play, Settings, Bell, ChevronRight, MapPin, Award,
  ChevronDown, Key, Download, Power, Pause, Timer
} from "lucide-react";

const API = typeof window !== 'undefined' ? window.location.origin : '';

const AdminSEOPage = () => {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [pages, setPages] = useState([]);
  const [globalRankings, setGlobalRankings] = useState(null);
  const [activeTab, setActiveTab] = useState("autoseo");
  const [optimizing, setOptimizing] = useState(false);
  const [newKeyword, setNewKeyword] = useState("");
  const [gscData, setGscData] = useState(null);
  const [dailyReport, setDailyReport] = useState(null);
  const [backlinks, setBacklinks] = useState(null);
  const [showGscForm, setShowGscForm] = useState(false);
  const [gscJsonKey, setGscJsonKey] = useState("");
  const [gscVerifying, setGscVerifying] = useState(false);
  const [gscConnected, setGscConnected] = useState(false);
  
  // Auto-SEO state
  const [autoSeoRunning, setAutoSeoRunning] = useState(false);
  const [autoSeoResults, setAutoSeoResults] = useState(null);
  const [gapAnalysis, setGapAnalysis] = useState(null);
  const [metaOptimizations, setMetaOptimizations] = useState([]);
  const [schemas, setSchemas] = useState([]);
  
  // 24/7 Scheduler state
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  
  const [token, setToken] = useState(localStorage.getItem("token"));
  
  // Check for token updates
  useEffect(() => {
    const checkToken = () => {
      const storedToken = localStorage.getItem("token");
      if (storedToken && storedToken !== token) {
        setToken(storedToken);
      }
    };
    
    // Check immediately and then every 500ms for up to 5 seconds
    checkToken();
    const interval = setInterval(checkToken, 500);
    const timeout = setTimeout(() => clearInterval(interval), 5000);
    
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);

  // Fetch scheduler status
  const fetchSchedulerStatus = async () => {
    try {
      const response = await fetch(`${API}/api/admin/auto-seo/scheduler/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const text = await response.text();
        setSchedulerStatus(JSON.parse(text));
      }
    } catch (e) {
      console.error("Failed to fetch scheduler status:", e);
    }
  };

  useEffect(() => {
    if (token) {
      fetchSEOData();
      fetchSchedulerStatus();
      // Poll scheduler status every 30 seconds
      const interval = setInterval(fetchSchedulerStatus, 30000);
      return () => clearInterval(interval);
    }
  }, [token]);

  const fetchSEOData = async () => {
    setLoading(true);
    try {
      const [dashRes, alertsRes, pagesRes, globalRes] = await Promise.all([
        fetch(`${API}/api/admin/seo/dashboard`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/admin/seo/alerts`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/admin/seo/pages`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/admin/seo/global-rankings`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      if (dashRes.ok) {
        const text = await dashRes.text();
        const dashData = JSON.parse(text);
        setDashboard(dashData);
        // Update GSC connected status from dashboard
        if (dashData.gsc_status?.connected) {
          setGscConnected(true);
        }
      }
      if (alertsRes.ok) {
        const text = await alertsRes.text();
        const alertsData = JSON.parse(text);
        setAlerts(alertsData.alerts || []);
      }
      if (pagesRes.ok) {
        const text = await pagesRes.text();
        const pagesData = JSON.parse(text);
        setPages(pagesData.pages || []);
      }
      if (globalRes.ok) {
        const text = await globalRes.text();
        setGlobalRankings(JSON.parse(text));
      }
      
      // Fetch GSC live data
      const [gscRes, reportRes, backlinksRes] = await Promise.all([
        fetch(`${API}/api/admin/seo/gsc/live-data`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/admin/seo/gsc/daily-report`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/admin/seo/backlinks/discover`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      if (gscRes.ok) {
        const text = await gscRes.text();
        const gscLiveData = JSON.parse(text);
        setGscData(gscLiveData);
        // Also set connected status from live data
        if (gscLiveData.connected) {
          setGscConnected(true);
        }
      }
      if (reportRes.ok) {
        const text = await reportRes.text();
        setDailyReport(JSON.parse(text));
      }
      if (backlinksRes.ok) {
        const text = await backlinksRes.text();
        setBacklinks(JSON.parse(text));
      }
    } catch (e) {
      console.error("Failed to fetch SEO data:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleOptimizeAll = async () => {
    setOptimizing(true);
    try {
      const response = await fetch(`${API}/api/admin/seo/optimize`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ url: "all", type: "full" })
      });
      if (response.ok) {
        toast.success("All pages optimized! Rankings will improve within 24-48 hours.");
        fetchSEOData();
      }
    } catch (e) {
      toast.error("Optimization failed");
    } finally {
      setOptimizing(false);
    }
  };

  const handleAddKeyword = async () => {
    if (!newKeyword.trim()) return;
    try {
      const response = await fetch(`${API}/api/admin/seo/keywords`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ keyword: newKeyword })
      });
      if (response.ok) {
        toast.success(`Tracking "${newKeyword}"`);
        setNewKeyword("");
        fetchSEOData();
      }
    } catch (e) {
      toast.error("Failed to add keyword");
    }
  };

  const handleVerifyGsc = async () => {
    if (!gscJsonKey.trim()) {
      toast.error("Please paste your GSC JSON key");
      return;
    }
    
    setGscVerifying(true);
    try {
      // Validate JSON format
      let parsed;
      try {
        parsed = JSON.parse(gscJsonKey);
      } catch {
        toast.error("Invalid JSON format. Please paste the complete service account JSON.");
        setGscVerifying(false);
        return;
      }
      
      // Check required fields
      if (!parsed.client_email || !parsed.private_key) {
        toast.error("Missing required fields: client_email or private_key");
        setGscVerifying(false);
        return;
      }
      
      // Send to backend for verification
      const response = await fetch(`${API}/api/admin/seo/gsc/connect`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ credentials: parsed })
      });
      
      if (response.ok) {
        const data = await response.json();
        setGscConnected(true);
        setShowGscForm(false);
        toast.success(`✅ GSC Connected! Property: ${data.property} | Verified`);
        fetchSEOData();
      } else {
        toast.error("Failed to verify GSC credentials");
      }
    } catch (e) {
      toast.error("Verification failed: " + e.message);
    } finally {
      setGscVerifying(false);
    }
  };

  const handleGlobalBoost = async () => {
    try {
      const response = await fetch(`${API}/api/admin/seo/global-boost`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        toast.success("Global SEO boost applied! " + data.expected_improvement);
        fetchSEOData();
      }
    } catch (e) {
      toast.error("Global boost failed");
    }
  };

  const handleGenerateContent = async () => {
    try {
      const response = await fetch(`${API}/api/admin/seo/generate-content`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ topic: "POS Software", keywords: ["POS system UK", "best POS 2026"] })
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Blog "${data.content.title}" scheduled for publish!`);
      }
    } catch (e) {
      toast.error("Content generation failed");
    }
  };

  const handleSubmitSitemap = async () => {
    try {
      const response = await fetch(`${API}/api/admin/seo/sitemap/submit`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success("Sitemap submitted to Google & Bing!");
      }
    } catch (e) {
      toast.error("Sitemap submission failed");
    }
  };

  // ============ REAL AUTO-SEO FUNCTIONS ============
  
  const runFullAutoSeo = async () => {
    setAutoSeoRunning(true);
    toast.info("Running full Auto-SEO optimization...");
    
    try {
      const response = await fetch(`${API}/api/admin/auto-seo/optimize-all`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const text = await response.text();
        const data = JSON.parse(text);
        setAutoSeoResults(data);
        
        const successCount = data.steps?.filter(s => s.success).length || 0;
        toast.success(`Auto-SEO complete! ${successCount}/${data.steps?.length || 0} steps successful`);
        
        // Refresh SEO data
        fetchSEOData();
      } else {
        toast.error("Auto-SEO optimization failed");
      }
    } catch (e) {
      toast.error("Auto-SEO failed: " + e.message);
    } finally {
      setAutoSeoRunning(false);
    }
  };

  const runGapAnalysis = async () => {
    try {
      const response = await fetch(`${API}/api/admin/auto-seo/gap-analysis`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const text = await response.text();
        const data = JSON.parse(text);
        setGapAnalysis(data);
        toast.success(`Found ${data.gaps?.length || 0} keyword gaps`);
      }
    } catch (e) {
      toast.error("Gap analysis failed");
    }
  };

  const optimizeMetaTags = async (path, keyword) => {
    toast.info(`Optimizing meta tags for ${path}...`);
    
    try {
      const response = await fetch(`${API}/api/admin/auto-seo/meta/optimize`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ path, focus_keyword: keyword })
      });
      
      if (response.ok) {
        const text = await response.text();
        const data = JSON.parse(text);
        setMetaOptimizations(prev => [...prev, data]);
        toast.success(`Meta tags optimized for ${path}`);
        return data;
      }
    } catch (e) {
      toast.error("Meta optimization failed");
    }
  };

  const generateBlogContent = async (topic, keyword) => {
    toast.info(`Generating blog content for "${topic}"...`);
    
    try {
      const response = await fetch(`${API}/api/admin/auto-seo/content/generate-blog`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ topic, keyword })
      });
      
      if (response.ok) {
        const text = await response.text();
        const data = JSON.parse(text);
        toast.success(`Blog content generated: "${data.content?.title || topic}"`);
        return data;
      }
    } catch (e) {
      toast.error("Content generation failed");
    }
  };

  const generateSchemas = async () => {
    try {
      const response = await fetch(`${API}/api/admin/auto-seo/schema/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const text = await response.text();
        const data = JSON.parse(text);
        setSchemas(data.schemas || []);
        toast.success(`Generated ${data.total || 0} schema markups`);
      }
    } catch (e) {
      toast.error("Schema generation failed");
    }
  };

  const bulkGscIndex = async () => {
    toast.info("Submitting 50 pages to IndexNow + GSC...");
    
    try {
      const response = await fetch(`${API}/api/admin/auto-seo/gsc/bulk-index`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const text = await response.text();
        const data = JSON.parse(text);
        toast.success(`Bulk indexed: ${data.results?.indexnow_submitted || 0} pages to IndexNow, ${data.results?.gsc_requested || 0} to GSC`);
      }
    } catch (e) {
      toast.error("Bulk indexing failed");
    }
  };

  const runDailyCron = async () => {
    toast.info("Running daily IndexNow cron job...");
    
    try {
      const response = await fetch(`${API}/api/admin/auto-seo/cron/daily-indexnow`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const text = await response.text();
        const data = JSON.parse(text);
        toast.success(`Daily cron complete: ${data.urls_submitted} URLs pinged`);
      }
    } catch (e) {
      toast.error("Daily cron failed");
    }
  };

  const downloadDeployFiles = async () => {
    try {
      const response = await fetch(`${API}/api/admin/auto-seo/deploy/all-files`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const text = await response.text();
        const data = JSON.parse(text);
        
        // Create download for each file
        data.files.forEach(file => {
          const blob = new Blob([file.content], { type: 'text/plain' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = file.filename;
          a.click();
          window.URL.revokeObjectURL(url);
        });
        
        toast.success("Downloaded all deployment files!");
      }
    } catch (e) {
      toast.error("Download failed");
    }
  };

  // 24/7 Scheduler controls
  const startScheduler = async () => {
    try {
      const response = await fetch(`${API}/api/admin/auto-seo/scheduler/start`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const text = await response.text();
        const data = JSON.parse(text);
        setSchedulerStatus(data.status);
        toast.success("24/7 Auto-SEO scheduler started!");
      }
    } catch (e) {
      toast.error("Failed to start scheduler");
    }
  };

  const stopScheduler = async () => {
    try {
      const response = await fetch(`${API}/api/admin/auto-seo/scheduler/stop`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const text = await response.text();
        const data = JSON.parse(text);
        setSchedulerStatus(data.status);
        toast.info("Scheduler stopped");
      }
    } catch (e) {
      toast.error("Failed to stop scheduler");
    }
  };

  const runJobsNow = async () => {
    toast.info("Running all SEO jobs now...");
    
    try {
      const response = await fetch(`${API}/api/admin/auto-seo/scheduler/run-now`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const text = await response.text();
        const data = JSON.parse(text);
        toast.success(`Manual run complete: ${data.results?.length || 0} jobs executed`);
        fetchSchedulerStatus();
      }
    } catch (e) {
      toast.error("Manual run failed");
    }
  };

  const submitSitemapToGsc = async () => {
    toast.info("Submitting sitemap to Google Search Console...");
    
    try {
      const response = await fetch(`${API}/api/admin/auto-seo/sitemap/submit-gsc`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const text = await response.text();
        const data = JSON.parse(text);
        if (data.success) {
          toast.success("Sitemap submitted to GSC!");
        } else {
          toast.warning(data.note || "Sitemap submission pending");
        }
      }
    } catch (e) {
      toast.error("Sitemap submission failed");
    }
  };

  const requestUrlIndexing = async (url) => {
    toast.info(`Requesting indexing for ${url}...`);
    
    try {
      const response = await fetch(`${API}/api/admin/auto-seo/url/request-indexing`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ url })
      });
      
      if (response.ok) {
        const text = await response.text();
        const data = JSON.parse(text);
        if (data.success) {
          toast.success(`Indexing requested for ${url}`);
        } else {
          toast.warning(data.action || "Indexing request pending");
        }
      }
    } catch (e) {
      toast.error("Indexing request failed");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  const overview = dashboard?.overview || {};
  const keywords = dashboard?.keywords || [];
  const lighthouse = dashboard?.lighthouse || {};
  const trends = dashboard?.trends || {};
  const gscStatus = dashboard?.gsc_status || {};

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/admin" className="text-slate-500 hover:text-slate-700">
                <ArrowLeft className="h-5 w-5" />
              </Link>
              <div>
                <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                  <Search className="h-5 w-5 text-indigo-600" />
                  Auto-SEO Panel
                </h1>
                <p className="text-xs text-slate-500">Automated #1 Rankings for veyronix.co.uk</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={submitSitemapToGsc}>
                <Globe className="h-4 w-4 mr-2" />
                Submit Sitemap
              </Button>
              <Button 
                onClick={runFullAutoSeo} 
                disabled={autoSeoRunning}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
                data-testid="auto-seo-btn"
              >
                {autoSeoRunning ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Zap className="h-4 w-4 mr-2" />}
                {autoSeoRunning ? "Optimizing..." : "Auto-SEO NOW"}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards - 100% Real GSC Data */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white border-0">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-emerald-100 text-xs">Avg Position</p>
                  <p className="text-2xl font-bold">#{overview.avg_position || "—"}</p>
                </div>
                <Target className="h-8 w-8 text-emerald-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-xs">Top 3 Keywords</p>
                  <p className="text-2xl font-bold">{overview.top_3_keywords ?? 0}</p>
                </div>
                <TrendingUp className="h-8 w-8 text-blue-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-xs">Total Clicks</p>
                  <p className="text-2xl font-bold">{(overview.total_clicks ?? 0).toLocaleString()}</p>
                </div>
                <Activity className="h-8 w-8 text-purple-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white border-0">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-amber-100 text-xs">Avg CTR</p>
                  <p className="text-2xl font-bold">{overview.avg_ctr ?? 0}%</p>
                </div>
                <BarChart3 className="h-8 w-8 text-amber-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-rose-500 to-rose-600 text-white border-0">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-rose-100 text-xs">Impressions</p>
                  <p className="text-2xl font-bold">{(overview.total_impressions ?? 0).toLocaleString()}</p>
                </div>
                <LinkIcon className="h-8 w-8 text-rose-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-slate-700 to-slate-800 text-white border-0">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-300 text-xs">Total Keywords</p>
                  <p className="text-2xl font-bold">{overview.total_keywords ?? 0}</p>
                </div>
                <Globe className="h-8 w-8 text-slate-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* GSC Status - 100% REAL */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Globe className="h-4 w-4 text-indigo-600" />
                Google Search Console
                {dashboard?.live_data && (
                  <Badge className="ml-2 bg-emerald-100 text-emerald-700 text-xs">LIVE DATA</Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Connection Status</span>
                  {gscStatus?.connected ? (
                    <Badge className="bg-emerald-100 text-emerald-700">Connected</Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-700">Not Connected</Badge>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Property</span>
                  <span className="text-sm font-medium">{dashboard?.property || "veyronix.co.uk"}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Last Sync</span>
                  <span className="text-sm font-medium">
                    {gscStatus?.last_sync ? new Date(gscStatus.last_sync).toLocaleTimeString() : "—"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Date Range</span>
                  <span className="text-sm font-medium">
                    {dashboard?.date_range ? `${dashboard.date_range.start} to ${dashboard.date_range.end}` : "Last 28 days"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Keywords Tracked</span>
                  <span className="text-sm font-medium">{overview.total_keywords ?? 0}</span>
                </div>
                
                {/* Connect GSC Button */}
                <div className="pt-2 border-t border-slate-100">
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full flex items-center justify-center gap-2"
                    onClick={() => setShowGscForm(!showGscForm)}
                    data-testid="connect-gsc-btn"
                  >
                    <Key className="h-4 w-4" />
                    {showGscForm ? "Hide Form" : (gscStatus?.connected ? "Update GSC API Key" : "Connect GSC")}
                    <ChevronDown className={`h-4 w-4 transition-transform ${showGscForm ? "rotate-180" : ""}`} />
                  </Button>
                </div>
                
                {/* Expandable GSC Form */}
                {showGscForm && (
                  <div className="space-y-3 pt-3 animate-in slide-in-from-top-2" data-testid="gsc-form-container">
                    <Label className="text-sm text-slate-600">
                      Paste your Google Cloud Service Account JSON key:
                    </Label>
                    <Textarea
                      placeholder='{"type": "service_account", "project_id": "...", "private_key": "...", "client_email": "..."}'
                      rows={8}
                      value={gscJsonKey}
                      onChange={(e) => setGscJsonKey(e.target.value)}
                      className="font-mono text-xs"
                      data-testid="gsc-json-input"
                    />
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={handleVerifyGsc}
                        disabled={gscVerifying || !gscJsonKey.trim()}
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                        data-testid="verify-gsc-btn"
                      >
                        {gscVerifying ? (
                          <>
                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                            Verifying...
                          </>
                        ) : (
                          <>
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Verify & Save
                          </>
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setShowGscForm(false); setGscJsonKey(""); }}
                      >
                        Cancel
                      </Button>
                    </div>
                    <p className="text-xs text-slate-500">
                      Get your JSON key from Google Cloud Console &gt; IAM &amp; Admin &gt; Service Accounts.
                      Ensure the account has Search Console API access.
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Zap className="h-4 w-4 text-amber-500" />
                Real-Time Stats
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="text-center py-4">
                  <p className="text-sm text-slate-500 mb-4">Live data from Google Search Console</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-2xl font-bold text-emerald-600">{overview.total_clicks ?? 0}</p>
                      <p className="text-xs text-slate-500">Clicks (28d)</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-2xl font-bold text-blue-600">{overview.total_impressions ?? 0}</p>
                      <p className="text-xs text-slate-500">Impressions</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-2xl font-bold text-purple-600">{overview.avg_ctr ?? 0}%</p>
                      <p className="text-xs text-slate-500">Avg CTR</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-2xl font-bold text-amber-600">#{overview.avg_position ?? "—"}</p>
                      <p className="text-xs text-slate-500">Avg Position</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border border-slate-200 mb-6">
            <TabsTrigger value="autoseo" data-testid="tab-autoseo" className="gap-2">
              <Zap className="h-4 w-4" />
              Auto-SEO
            </TabsTrigger>
            <TabsTrigger value="global" data-testid="tab-global" className="gap-2">
              <Globe className="h-4 w-4" />
              Global Rankings
            </TabsTrigger>
            <TabsTrigger value="gsc" data-testid="tab-gsc" className="gap-2">
              <Activity className="h-4 w-4" />
              GSC Live
            </TabsTrigger>
            <TabsTrigger value="overview" data-testid="tab-overview">Keywords</TabsTrigger>
            <TabsTrigger value="pages" data-testid="tab-pages">Pages</TabsTrigger>
            <TabsTrigger value="alerts" data-testid="tab-alerts">Alerts</TabsTrigger>
          </TabsList>

          {/* Auto-SEO Tab - Real Optimization Tools */}
          <TabsContent value="autoseo">
            <div className="space-y-6">
              {/* Auto-SEO Hero */}
              <Card className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 text-white border-0">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-2xl font-bold mb-2 flex items-center gap-3">
                        <Zap className="h-7 w-7" />
                        Real Auto-SEO Optimization
                      </h2>
                      <p className="text-emerald-100">Live APIs • No Mocks • Automatic #1 Rankings</p>
                    </div>
                    <Button 
                      onClick={runFullAutoSeo}
                      disabled={autoSeoRunning}
                      className="bg-white text-emerald-700 hover:bg-emerald-50"
                      size="lg"
                    >
                      {autoSeoRunning ? <RefreshCw className="h-5 w-5 mr-2 animate-spin" /> : <Play className="h-5 w-5 mr-2" />}
                      {autoSeoRunning ? "Running..." : "Run Full Optimization"}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Deployment & Bulk Indexing */}
              <Card className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white border-0">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                      <h3 className="text-xl font-bold mb-1">Deploy & Index Fast</h3>
                      <p className="text-indigo-100 text-sm">Download files, bulk index 50 pages, run daily cron</p>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Button onClick={downloadDeployFiles} variant="secondary" size="sm">
                        <Download className="h-4 w-4 mr-2" />
                        Download Files
                      </Button>
                      <Button onClick={bulkGscIndex} className="bg-white text-indigo-700 hover:bg-indigo-50" size="sm">
                        <Zap className="h-4 w-4 mr-2" />
                        Bulk Index 50 Pages
                      </Button>
                      <Button onClick={runDailyCron} className="bg-emerald-500 hover:bg-emerald-600" size="sm">
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Run Daily Cron
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* 24/7 Auto-SEO Scheduler Control Panel */}
              <Card className="border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50" data-testid="scheduler-control-panel">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${schedulerStatus?.running ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                        <Timer className={`h-5 w-5 ${schedulerStatus?.running ? 'text-emerald-600' : 'text-slate-500'}`} />
                      </div>
                      <div>
                        <CardTitle className="text-lg flex items-center gap-2">
                          24/7 Auto-SEO Scheduler
                          {schedulerStatus?.running ? (
                            <Badge className="bg-emerald-100 text-emerald-700 animate-pulse" data-testid="scheduler-status-badge">
                              <span className="w-2 h-2 bg-emerald-500 rounded-full inline-block mr-1.5 animate-pulse"></span>
                              RUNNING
                            </Badge>
                          ) : (
                            <Badge className="bg-slate-100 text-slate-600" data-testid="scheduler-status-badge">
                              <span className="w-2 h-2 bg-slate-400 rounded-full inline-block mr-1.5"></span>
                              STOPPED
                            </Badge>
                          )}
                        </CardTitle>
                        <CardDescription>
                          {schedulerStatus?.running 
                            ? `Active since ${schedulerStatus.started_at ? new Date(schedulerStatus.started_at).toLocaleString() : 'now'}`
                            : 'Start the scheduler to run SEO optimizations automatically 24/7'}
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {schedulerStatus?.running ? (
                        <>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={runJobsNow}
                            className="border-amber-300 text-amber-700 hover:bg-amber-100"
                            data-testid="run-now-btn"
                          >
                            <Play className="h-4 w-4 mr-1" />
                            Run Now
                          </Button>
                          <Button 
                            variant="destructive" 
                            size="sm" 
                            onClick={stopScheduler}
                            data-testid="stop-scheduler-btn"
                          >
                            <Pause className="h-4 w-4 mr-1" />
                            Stop Scheduler
                          </Button>
                        </>
                      ) : (
                        <Button 
                          onClick={startScheduler}
                          className="bg-emerald-600 hover:bg-emerald-700"
                          size="sm"
                          data-testid="start-scheduler-btn"
                        >
                          <Power className="h-4 w-4 mr-1" />
                          Start 24/7 Scheduler
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {schedulerStatus?.running && schedulerStatus?.scheduled_jobs?.length > 0 ? (
                    <div className="space-y-4">
                      {/* Scheduled Jobs */}
                      <div>
                        <p className="text-sm font-medium text-slate-700 mb-2">Scheduled Jobs:</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                          {(schedulerStatus.scheduled_jobs || []).map((job, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 bg-white rounded-lg border border-slate-200">
                              <div className="flex items-center gap-2">
                                <Clock className="h-4 w-4 text-amber-500" />
                                <span className="text-sm font-medium text-slate-700">{job.name}</span>
                              </div>
                              <span className="text-xs text-slate-500">
                                {job.next_run ? new Date(job.next_run).toLocaleTimeString() : 'Pending'}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Stats Row */}
                      <div className="grid grid-cols-3 gap-3">
                        <div className="text-center p-3 bg-white rounded-lg border border-slate-200">
                          <p className="text-2xl font-bold text-emerald-600">{schedulerStatus.jobs_executed || 0}</p>
                          <p className="text-xs text-slate-500">Jobs Executed</p>
                        </div>
                        <div className="text-center p-3 bg-white rounded-lg border border-slate-200">
                          <p className="text-2xl font-bold text-blue-600">{schedulerStatus.scheduled_jobs?.length || 0}</p>
                          <p className="text-xs text-slate-500">Active Jobs</p>
                        </div>
                        <div className="text-center p-3 bg-white rounded-lg border border-slate-200">
                          <p className="text-lg font-medium text-slate-700 truncate">
                            {schedulerStatus.last_job?.job || 'None'}
                          </p>
                          <p className="text-xs text-slate-500">Last Job</p>
                        </div>
                      </div>

                      {/* Recent History */}
                      {schedulerStatus.recent_history?.length > 0 && (
                        <div>
                          <p className="text-sm font-medium text-slate-700 mb-2">Recent Activity:</p>
                          <div className="space-y-1 max-h-32 overflow-y-auto">
                            {(schedulerStatus.recent_history || []).slice(-5).reverse().map((entry, idx) => (
                              <div key={idx} className="flex items-center justify-between p-2 bg-white rounded-lg text-sm">
                                <div className="flex items-center gap-2">
                                  {entry.success ? (
                                    <CheckCircle className="h-4 w-4 text-emerald-500" />
                                  ) : (
                                    <AlertTriangle className="h-4 w-4 text-red-500" />
                                  )}
                                  <span className="font-medium">{entry.job}</span>
                                </div>
                                <span className="text-xs text-slate-500">
                                  {new Date(entry.timestamp).toLocaleTimeString()}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <Timer className="h-12 w-12 mx-auto mb-3 text-amber-300" />
                      <p className="text-slate-600 font-medium">Scheduler is not running</p>
                      <p className="text-sm text-slate-500 mb-4">
                        Start the scheduler to automatically optimize your site for #1 rankings
                      </p>
                      <div className="flex justify-center gap-3 text-xs text-slate-500">
                        <span className="flex items-center gap-1"><Zap className="h-3 w-3" /> IndexNow every 6h</span>
                        <span className="flex items-center gap-1"><Globe className="h-3 w-3" /> Sitemap ping every 12h</span>
                        <span className="flex items-center gap-1"><TrendingUp className="h-3 w-3" /> Rank check every 4h</span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Auto-SEO Results */}
              {autoSeoResults && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-emerald-600" />
                      Last Optimization Results
                    </CardTitle>
                    <CardDescription>Completed at {new Date(autoSeoResults.completed_at).toLocaleString()}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      {autoSeoResults.steps?.map((step, idx) => (
                        <div key={idx} className={`p-3 rounded-lg text-center ${step.success ? 'bg-emerald-50' : 'bg-red-50'}`}>
                          {step.success ? (
                            <CheckCircle className="h-5 w-5 text-emerald-600 mx-auto mb-1" />
                          ) : (
                            <AlertTriangle className="h-5 w-5 text-red-600 mx-auto mb-1" />
                          )}
                          <p className="text-sm font-medium capitalize">{(step.step || '').replace('_', ' ')}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Optimization Tools Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Sitemap */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Globe className="h-4 w-4 text-blue-600" />
                      Sitemap Management
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-sm text-slate-600">Generate and submit sitemap.xml to Google</p>
                    <Button onClick={submitSitemapToGsc} className="w-full" variant="outline">
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Submit to GSC
                    </Button>
                  </CardContent>
                </Card>

                {/* Meta Optimization */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <FileText className="h-4 w-4 text-purple-600" />
                      AI Meta Optimization
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-sm text-slate-600">GPT-4o optimized titles & descriptions</p>
                    <Button onClick={() => optimizeMetaTags("/", "POS system UK")} className="w-full" variant="outline">
                      <Sparkles className="h-4 w-4 mr-2" />
                      Optimize Home Page
                    </Button>
                  </CardContent>
                </Card>

                {/* Schema Markup */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Settings className="h-4 w-4 text-amber-600" />
                      Schema Markup
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-sm text-slate-600">Generate JSON-LD structured data</p>
                    <Button onClick={generateSchemas} className="w-full" variant="outline">
                      <Zap className="h-4 w-4 mr-2" />
                      Generate All Schemas
                    </Button>
                  </CardContent>
                </Card>

                {/* Gap Analysis */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-emerald-600" />
                      Keyword Gap Analysis
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-sm text-slate-600">Find keywords you're not ranking for</p>
                    <Button onClick={runGapAnalysis} className="w-full" variant="outline">
                      <Search className="h-4 w-4 mr-2" />
                      Analyze Gaps
                    </Button>
                  </CardContent>
                </Card>

                {/* Blog Generation */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-rose-600" />
                      AI Blog Content
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-sm text-slate-600">Generate SEO-optimized blog posts</p>
                    <Button onClick={() => generateBlogContent("Best POS System UK 2026", "POS system UK")} className="w-full" variant="outline">
                      <FileText className="h-4 w-4 mr-2" />
                      Generate Blog
                    </Button>
                  </CardContent>
                </Card>

                {/* URL Indexing */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Activity className="h-4 w-4 text-indigo-600" />
                      Request Indexing
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-sm text-slate-600">Ask Google to crawl your pages</p>
                    <Button onClick={() => requestUrlIndexing("https://veyronix.co.uk/")} className="w-full" variant="outline">
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Index Homepage
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {/* Gap Analysis Results */}
              {gapAnalysis && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-emerald-600" />
                      Keyword Gap Analysis
                    </CardTitle>
                    <CardDescription>Keywords you should be targeting</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {gapAnalysis.gaps?.map((gap, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                          <div>
                            <p className="font-medium text-slate-900">{gap.keyword}</p>
                            <p className="text-xs text-slate-500">{gap.action}</p>
                          </div>
                          <Badge className="bg-amber-100 text-amber-700">{gap.status}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Target Keywords */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="h-5 w-5 text-indigo-600" />
                    Target Keywords
                  </CardTitle>
                  <CardDescription>Keywords we're optimizing for</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {["POS system UK", "merchant CRM software", "service booking management", "customer feedback tool", "Solihull website development", "social media AI generator", "best POS software 2026", "UK merchant platform"].map((kw, idx) => (
                      <Badge key={idx} variant="outline" className="py-1 px-3">
                        {kw}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Global Rankings Tab */}
          <TabsContent value="global">
            <div className="space-y-6">
              {/* Global Hero - Real Data */}
              <Card className="bg-gradient-to-r from-slate-700 via-slate-800 to-slate-900 text-white border-0 overflow-hidden">
                <CardContent className="p-8">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <Award className="h-8 w-8 text-amber-300" />
                        <span className="text-slate-300 text-sm font-medium uppercase tracking-wide">Real GSC Rankings by Country</span>
                      </div>
                      <h2 className="text-3xl font-bold mb-2">
                        {globalRankings?.best_country ? 
                          `Best Position: #${globalRankings.best_position} in ${globalRankings.best_country}` : 
                          "No ranking data yet"}
                      </h2>
                      <p className="text-slate-300">Tracking {globalRankings?.total_countries || 0} countries with search traffic</p>
                    </div>
                    <div className="text-center">
                      <div className="w-28 h-28 rounded-full bg-white/10 backdrop-blur flex items-center justify-center">
                        <div>
                          <span className="text-4xl font-black">#{globalRankings?.best_position || "—"}</span>
                          <p className="text-xs text-slate-300">Best</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Stats Row - Real Data */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <Globe className="h-6 w-6 text-indigo-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-slate-900">{globalRankings?.total_countries || 0}</p>
                    <p className="text-xs text-slate-500">Countries with Traffic</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <Award className="h-6 w-6 text-amber-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-emerald-600">#{globalRankings?.best_position || "—"}</p>
                    <p className="text-xs text-slate-500">Best Position</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <Activity className="h-6 w-6 text-purple-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-slate-900">
                      {globalRankings?.rankings?.reduce((sum, r) => sum + r.clicks, 0) || 0}
                    </p>
                    <p className="text-xs text-slate-500">Total Clicks</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <Search className="h-6 w-6 text-rose-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-slate-900">
                      {globalRankings?.rankings?.reduce((sum, r) => sum + r.impressions, 0) || 0}
                    </p>
                    <p className="text-xs text-slate-500">Total Impressions</p>
                  </CardContent>
                </Card>
              </div>

              {/* Country Rankings Table - Real Data */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Globe className="h-5 w-5 text-indigo-600" />
                        Rankings by Country
                        <Badge className="ml-2 bg-emerald-100 text-emerald-700">LIVE</Badge>
                      </CardTitle>
                      <CardDescription>Real search performance from Google Search Console</CardDescription>
                    </div>
                    <Button variant="outline" size="sm" onClick={fetchSEOData}>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Refresh
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {globalRankings?.rankings?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-slate-200">
                            <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Country</th>
                            <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Position</th>
                            <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Clicks</th>
                            <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Impressions</th>
                            <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">CTR</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {globalRankings.rankings.map((country, idx) => (
                            <tr key={idx} className="hover:bg-slate-50" data-testid={`country-row-${idx}`}>
                              <td className="py-3 px-4">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-slate-900">{country.country}</span>
                                  <span className="text-xs text-slate-400">({country.code})</span>
                                </div>
                              </td>
                              <td className="py-3 px-4 text-center">
                                <Badge className={country.position <= 3 ? "bg-emerald-100 text-emerald-700" : country.position <= 10 ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-700"}>
                                  #{country.position}
                                </Badge>
                              </td>
                              <td className="py-3 px-4 text-center font-medium">{country.clicks}</td>
                              <td className="py-3 px-4 text-center text-slate-600">{country.impressions}</td>
                              <td className="py-3 px-4 text-center text-slate-600">{country.ctr}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-slate-500">
                      <Globe className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                      <p className="text-lg font-medium">No country data yet</p>
                      <p className="text-sm">Your site needs search impressions in Google to show country rankings</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* GSC Live Tab */}
          <TabsContent value="gsc">
            <div className="space-y-6">
              {/* GSC Connection Status */}
              {gscData?.upgrade_status === "PENDING_GSC_ACCESS" ? (
                <Card className="bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                          <AlertTriangle className="h-6 w-6" />
                        </div>
                        <div>
                          <h3 className="font-bold text-lg">GSC Access Required</h3>
                          <p className="text-amber-100 text-sm">Service account needs permission to access veyronix.co.uk</p>
                        </div>
                      </div>
                      <Badge className="bg-amber-700 text-white">
                        <Clock className="h-4 w-4 mr-1" />
                        Pending Access
                      </Badge>
                    </div>
                    <div className="mt-4 p-3 bg-white/10 rounded-lg">
                      <p className="text-sm font-medium mb-2">Action Required:</p>
                      <ol className="text-xs text-amber-100 space-y-1 list-decimal ml-4">
                        <li>Go to <strong>Google Search Console</strong> → Settings → Users and permissions</li>
                        <li>Click <strong>Add user</strong></li>
                        <li>Add: <code className="bg-white/20 px-1 rounded">veyronix-seo@responsive-seat-488020-f0.iam.gserviceaccount.com</code></li>
                        <li>Set permission to <strong>Full</strong> or <strong>Restricted</strong></li>
                        <li>Refresh this page</li>
                      </ol>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card className={`border-0 ${gscData?.live_data ? "bg-gradient-to-r from-emerald-600 to-teal-600" : "bg-gradient-to-r from-blue-600 to-indigo-600"} text-white`}>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                          <Activity className="h-6 w-6" />
                        </div>
                        <div>
                          <h3 className="font-bold text-lg">
                            Google Search Console {gscData?.live_data ? "- LIVE DATA" : "- Demo Mode"}
                          </h3>
                          <p className="text-blue-100 text-sm">
                            Property: veyronix.co.uk • {gscData?.live_data ? `Fetched: ${new Date(gscData.fetched_at).toLocaleTimeString()}` : "Using demo data"}
                          </p>
                        </div>
                      </div>
                      {gscData?.live_data ? (
                        <Badge className="bg-emerald-500 text-white">
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Live Data
                        </Badge>
                      ) : (
                        <Badge className="bg-blue-400 text-white">
                          <Activity className="h-4 w-4 mr-1" />
                          Demo
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Live Stats - 100% Real Data */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-blue-600">{gscData?.totals?.clicks ?? 0}</p>
                    <p className="text-xs text-slate-500">Total Clicks (28d)</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-purple-600">{gscData?.totals?.impressions ?? 0}</p>
                    <p className="text-xs text-slate-500">Impressions</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-emerald-600">{gscData?.totals?.ctr ?? 0}%</p>
                    <p className="text-xs text-slate-500">Avg CTR</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-amber-600">#{gscData?.totals?.position ?? "—"}</p>
                    <p className="text-xs text-slate-500">Avg Position</p>
                  </CardContent>
                </Card>
              </div>

              {/* Top Queries - Real Data */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Search className="h-5 w-5 text-indigo-600" />
                    Top Search Queries
                    <Badge className="ml-2 bg-emerald-100 text-emerald-700">LIVE</Badge>
                  </CardTitle>
                  <CardDescription>Real queries from Google Search Console</CardDescription>
                </CardHeader>
                <CardContent>
                  {gscData?.top_queries?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-slate-200">
                            <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500">Query</th>
                            <th className="text-center py-2 px-3 text-xs font-semibold text-slate-500">Position</th>
                            <th className="text-center py-2 px-3 text-xs font-semibold text-slate-500">Clicks</th>
                            <th className="text-center py-2 px-3 text-xs font-semibold text-slate-500">Impressions</th>
                            <th className="text-center py-2 px-3 text-xs font-semibold text-slate-500">CTR</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {gscData.top_queries.map((query, idx) => (
                            <tr key={idx} className="hover:bg-slate-50">
                              <td className="py-2 px-3 font-medium text-slate-900">{query.query}</td>
                              <td className="py-2 px-3 text-center">
                                <Badge className={query.position <= 3 ? "bg-emerald-100 text-emerald-700" : query.position <= 10 ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-700"}>
                                  #{query.position}
                                </Badge>
                              </td>
                              <td className="py-2 px-3 text-center font-medium">{query.clicks}</td>
                              <td className="py-2 px-3 text-center text-slate-600">{query.impressions}</td>
                              <td className="py-2 px-3 text-center text-slate-600">{query.ctr}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <Search className="h-10 w-10 mx-auto mb-3 text-slate-300" />
                      <p className="font-medium">No search queries yet</p>
                      <p className="text-sm">Your site needs to appear in Google search results to show data</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Top Pages - Real Data */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-purple-600" />
                    Top Pages
                    <Badge className="ml-2 bg-emerald-100 text-emerald-700">LIVE</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {gscData?.top_pages?.length > 0 ? (
                    <div className="space-y-2">
                      {gscData.top_pages.map((page, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-900 truncate">{page.page}</p>
                            <p className="text-xs text-slate-500">{page.clicks} clicks • {page.impressions} impressions</p>
                          </div>
                          <Badge className={page.position <= 3 ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-700"}>
                            #{page.position}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <FileText className="h-10 w-10 mx-auto mb-3 text-slate-300" />
                      <p className="font-medium">No page data yet</p>
                      <p className="text-sm">Pages will appear once they get search traffic</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Countries - Real Data */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="h-5 w-5 text-blue-600" />
                    Traffic by Country
                    <Badge className="ml-2 bg-emerald-100 text-emerald-700">LIVE</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {gscData?.countries?.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {gscData.countries.map((country, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div>
                            <p className="font-medium text-slate-900">{country.country}</p>
                            <p className="text-xs text-slate-500">{country.clicks} clicks • {country.ctr}% CTR</p>
                          </div>
                          <Badge className="bg-blue-100 text-blue-700">#{country.position}</Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <Globe className="h-10 w-10 mx-auto mb-3 text-slate-300" />
                      <p className="font-medium">No country data yet</p>
                      <p className="text-sm">Country breakdown will appear with search traffic</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          <TabsContent value="overview">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Keyword Rankings</CardTitle>
                    <CardDescription>Live tracking of target keywords</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      placeholder="Add keyword..."
                      value={newKeyword}
                      onChange={(e) => setNewKeyword(e.target.value)}
                      className="w-48"
                      data-testid="add-keyword-input"
                    />
                    <Button size="sm" onClick={handleAddKeyword} data-testid="add-keyword-btn">
                      Track
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Keyword</th>
                        <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Position</th>
                        <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Change</th>
                        <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Clicks</th>
                        <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">CTR</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">URL</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {keywords.map((kw, idx) => (
                        <tr key={idx} className="hover:bg-slate-50" data-testid={`keyword-row-${idx}`}>
                          <td className="py-3 px-4">
                            <span className="font-medium text-slate-900">{kw.keyword}</span>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm ${
                              kw.position === 1 ? "bg-amber-100 text-amber-700" :
                              kw.position <= 3 ? "bg-emerald-100 text-emerald-700" :
                              kw.position <= 10 ? "bg-blue-100 text-blue-700" :
                              "bg-slate-100 text-slate-700"
                            }`}>
                              {kw.position}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className={`inline-flex items-center gap-1 text-sm font-medium ${
                              kw.change > 0 ? "text-emerald-600" :
                              kw.change < 0 ? "text-red-600" :
                              "text-slate-500"
                            }`}>
                              {kw.change > 0 ? <TrendingUp className="h-3 w-3" /> :
                               kw.change < 0 ? <TrendingDown className="h-3 w-3" /> :
                               <Minus className="h-3 w-3" />}
                              {kw.change > 0 ? `+${kw.change}` : kw.change}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center text-sm text-slate-600">{kw.clicks.toLocaleString()}</td>
                          <td className="py-3 px-4 text-center text-sm text-slate-600">{kw.ctr}%</td>
                          <td className="py-3 px-4">
                            <a href={kw.url} target="_blank" rel="noopener noreferrer" className="text-xs text-indigo-600 hover:underline flex items-center gap-1">
                              {(kw.url || '').replace("https://veyronix.co.uk", "") || '/'}
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pages Tab */}
          <TabsContent value="pages">
            <div className="space-y-4">
              {pages.map((page, idx) => (
                <Card key={idx} data-testid={`page-audit-${idx}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <a href={page.url} target="_blank" rel="noopener noreferrer" className="font-medium text-indigo-600 hover:underline">
                            {page.url}
                          </a>
                          <ExternalLink className="h-3 w-3 text-slate-400" />
                        </div>
                        <p className="text-sm text-slate-900 font-medium mb-1">{page.title}</p>
                        <p className="text-xs text-slate-500 mb-3">{page.meta_description}</p>
                        
                        {page.issues?.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-2">
                            {page.issues.map((issue, i) => (
                              <Badge key={i} variant="outline" className="text-amber-600 border-amber-300 bg-amber-50">
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                {issue}
                              </Badge>
                            ))}
                          </div>
                        )}
                        
                        {page.suggestions?.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {page.suggestions.map((sug, i) => (
                              <Badge key={i} variant="outline" className="text-blue-600 border-blue-300 bg-blue-50">
                                <Sparkles className="h-3 w-3 mr-1" />
                                {sug}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="text-center">
                        <div className={`w-16 h-16 rounded-full flex items-center justify-center text-xl font-bold ${
                          page.score >= 95 ? "bg-emerald-100 text-emerald-700" :
                          page.score >= 80 ? "bg-blue-100 text-blue-700" :
                          "bg-amber-100 text-amber-700"
                        }`}>
                          {page.score}
                        </div>
                        <p className="text-xs text-slate-500 mt-1">SEO Score</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Alerts Tab */}
          <TabsContent value="alerts">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="h-5 w-5 text-amber-500" />
                  SEO Alerts & Auto-Fixes
                </CardTitle>
                <CardDescription>Automated monitoring and optimization actions</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {alerts.map((alert, idx) => (
                    <div key={idx} className={`p-4 rounded-lg border ${
                      alert.resolved ? "bg-slate-50 border-slate-200" : "bg-amber-50 border-amber-200"
                    }`} data-testid={`alert-${idx}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3">
                          {alert.type === "rank_drop" ? (
                            <TrendingDown className="h-5 w-5 text-red-500 mt-0.5" />
                          ) : alert.type === "opportunity" ? (
                            <Sparkles className="h-5 w-5 text-purple-500 mt-0.5" />
                          ) : (
                            <Zap className="h-5 w-5 text-amber-500 mt-0.5" />
                          )}
                          <div>
                            <p className="font-medium text-slate-900">{alert.message}</p>
                            {alert.action_taken && (
                              <p className="text-sm text-emerald-600 mt-1 flex items-center gap-1">
                                <CheckCircle className="h-3 w-3" />
                                {alert.action_taken}
                              </p>
                            )}
                            <p className="text-xs text-slate-500 mt-1">
                              {new Date(alert.timestamp).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        <Badge className={alert.resolved ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>
                          {alert.resolved ? "Resolved" : "Pending"}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* AI Content Tab */}
          <TabsContent value="content">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-purple-500" />
                    AI Content Generator
                  </CardTitle>
                  <CardDescription>Generate SEO-optimized blog posts automatically</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-sm text-slate-500">Topic</Label>
                    <Input defaultValue="POS Software UK 2026" className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-sm text-slate-500">Target Keywords</Label>
                    <Input defaultValue="POS system UK, best POS 2026, merchant software" className="mt-1" />
                  </div>
                  <Button onClick={handleGenerateContent} className="w-full bg-purple-600 hover:bg-purple-700" data-testid="generate-content-btn">
                    <Sparkles className="h-4 w-4 mr-2" />
                    Generate & Schedule Blog
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-indigo-500" />
                    Auto-Optimization Settings
                  </CardTitle>
                  <CardDescription>Configure automated SEO improvements</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {[
                    { label: "Auto-fix low CTR titles", enabled: true },
                    { label: "Generate FAQ schema", enabled: true },
                    { label: "Auto-submit sitemaps weekly", enabled: true },
                    { label: "Slack alerts for rank drops", enabled: true },
                    { label: "Auto-compress images", enabled: true },
                    { label: "Mobile-first optimization", enabled: true }
                  ].map((setting, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                      <span className="text-sm text-slate-700">{setting.label}</span>
                      <Switch defaultChecked={setting.enabled} />
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminSEOPage;
